import api from './apiClient';

export type AdminUserId = string | number;

export interface ActiveStream {
  id: string;
  sourceId: string | null;
  pathName?: string;
  name?: string;
  channelId?: string;
  channelName?: string;
  channelActive?: boolean;
  billingSuspended?: boolean;
  sourceMode?: string;
  sourceLabel?: string;
  mediaBox?: {
    isEnabled?: boolean;
    playbackEnabled?: boolean;
    backupEnabled?: boolean;
    playAllAssets?: boolean;
    disabledReason?: string | null;
    usedBytes?: number;
    capacityBytes?: number;
    usagePercent?: number;
    assetCount?: number;
    readyAssetCount?: number;
    processingAssetCount?: number;
    failedAssetCount?: number;
    loopPlayback?: boolean;
    backupThresholdSecs?: number;
    uploadProfile?: string | null;
    serviceAvailable?: boolean;
    uploadGatewayAvailable?: boolean;
    assets?: Array<{
      id: string;
      label: string;
      status: string;
      statusLabel: string;
      stage?: string | null;
      progressPercent?: number | null;
      error?: string | null;
      fileSizeBytes?: number;
      bytesReceived?: number;
      durationSecs?: number | null;
      videoCodec?: string | null;
      audioCodec?: string | null;
      width?: number | null;
      height?: number | null;
      isCurrent?: boolean;
      isNext?: boolean;
      createdAt?: string | null;
      updatedAt?: string | null;
    }>;
  } | null;
  scheduledDeletion?: { effectiveAt?: string | null; reason?: string | null } | null;
  userId?: number;
  userHid?: string;
  userEmail?: string;
  userName?: string;
  accountStatus?: string;
  startedAt?: string | null;
  readyTime?: string | null;
  viewers: number;
  readers?: Array<{ id?: string | null; type?: string; remoteAddr?: string | null; bytesSent?: number }>;
  sourceType?: string;
  sourceReady?: boolean;
  bitrate?: number;
  audioCodec?: string;
  videoCodec?: string;
  width?: number;
  height?: number;
  fps?: number;
  bytesIn?: number;
  bytesOut?: number;
  duration?: number;
  uptime?: number;
  media?: { videoCodec?: string | null; audioCodec?: string | null; tracks?: number | null };
  outputs?: { total: number; active: number; direct: number; remapped: number; managed?: number; hls: number; hlsPlus: number; pull: number; social: number };
  outputLabels?: string[];
  sampledAt?: string;
  suspensionReason?: string | null;
  transport?: {
    protocol?: string | null;
    remoteAddress?: string | null;
    state?: string;
    packetsReceived?: number | null;
    packetsLost?: number | null;
    packetsRetransmitted?: number | null;
    packetLossPercent?: number | null;
    latencyMs?: number | null;
    jitterMs?: number | null;
    transportMetricsAvailable?: boolean;
    currentBandwidthMbps?: number | null;
    bandwidthCeilingMbps?: number;
    linkCapacityMbps?: number | null;
    maxBandwidthMbps?: number | null;
  };
  channel?: {
    id: string;
    name: string;
    createdAt?: string;
    updatedAt?: string;
    active?: boolean;
    billingSuspended?: boolean;
    suspensionReason?: string | null;
    sourceMode?: string;
    setupFeePaid?: boolean;
    outputPackageCode?: string | null;
    pendingOutputPackageCode?: string | null;
    epg?: { status?: string; lastUpdated?: string | null; xmltv?: boolean; gracenote?: boolean };
    limits?: Record<string, number>;
  };
  owner?: {
    id: string;
    name: string;
    email: string;
    accountStatus: string;
    previousStatus?: string | null;
    restriction?: { status?: string; reason?: string | null; at?: string | null } | null;
    country?: string | null;
    currency?: string | null;
    setupFeePaid?: boolean;
    trialExpiresAt?: string | null;
    billingCycleEndsAt?: string | null;
    gracePeriodEndsAt?: string | null;
  };
  billing?: {
    status?: string | null;
    canPublish?: boolean;
    canPush?: boolean;
    currentPeriodStart?: string | null;
    currentPeriodEnd?: string | null;
    graceEndsAt?: string | null;
    limits?: Record<string, number> | null;
  } | null;
  preview?: { available?: boolean; recommendedType?: 'webrtc' | 'hls' };
  playkaster?: {
    isActive?: boolean;
    tierCode?: string;
    purchasedAt?: string;
    expiresAt?: string | null;
    storageBytes?: number;
    usedBytes?: number;
    assetCount?: number;
    playlistCount?: number;
    assignedOutput?: string | null;
    playlists?: Array<{
      id: string;
      name: string;
      mode?: string;
      startDate?: string | null;
      clockStart?: string | null;
      itemCount?: number;
      updatedAt?: string;
      session?: { status?: string; currentItemPos?: number | null; startedAt?: string | null; endedAt?: string | null; errorMessage?: string | null; updatedAt?: string } | null;
    }>;
  } | null;
  outputDetails?: Array<{
    id?: string;
    active?: boolean;
    type?: string;
    mode?: string;
    protocol?: string;
    name?: string;
    destination?: string;
    label?: string;
    createdAt?: string | null;
    updatedAt?: string | null;
    health?: { status?: string; source?: string | null; updatedAt?: string | null; metrics?: Record<string, unknown> | null };
    configuration?: Record<string, string | number | boolean | null>;
  }>;
  status: 'live' | 'paused' | 'stopped' | 'error';
}

export interface DashboardSummary {
  users: { total: number; active: number; trial: number; awaiting?: number };
  protocolStats?: { rtmp: number; srt: number };
  streams?: any;
  accountCreation?: any;
  billing?: any;
  payments?: any;
  server: { cpuUsage: number; ramUsage: number; diskUsage: number; totalBandwidth: number };
}

export interface AdminReportsSummary {
  generatedAt: string;
  range: { weekStart: string; monthStart: string };
  users: { total: number; active: number; disabled: number; newThisMonth: number };
  channels: { total: number; active: number };
  billing: { revenueThisMonth: number; currency: string; pendingPayments: number; failedPayments: number; openInvoices: number };
  outputs: { direct: number; managed: number; hls: number; hlsPlus: number; total: number };
}

export type GoServiceState = 'operational' | 'degraded' | 'offline' | 'unknown';

export interface GoServiceStatus {
  id: string;
  name: string;
  group: string;
  unit: string;
  description?: string;
  status: GoServiceState;
  activeState: string;
  subState: string;
  healthStatus: 'healthy' | 'unhealthy' | 'checking' | 'not_applicable';
  healthLatencyMs?: number;
  message?: string;
  pid: number;
  uptimeSeconds: number;
  cpuPercent: number;
  memoryBytes: number;
  tasks: number;
  restarts: number;
  checkedAt: string;
}

export interface GoSystemStatus {
  generatedAt: string;
  pollSeconds: number;
  overall: {
    status: 'operational' | 'degraded' | 'outage';
    total: number;
    operational: number;
    degraded: number;
    offline: number;
    unknown: number;
  };
  host: {
    cpuPercent: number;
    memoryTotalBytes: number;
    memoryUsedBytes: number;
    memoryPercent: number;
    diskTotalBytes: number;
    diskUsedBytes: number;
    diskPercent: number;
    load1: number;
    load5: number;
    load15: number;
    uptimeSeconds: number;
  };
  services: GoServiceStatus[];
  history: Array<{
    serviceId: string;
    serviceName: string;
    from: GoServiceState;
    to: GoServiceState;
    occurredAt: string;
  }>;
}

export interface NocReportsSummary {
  generatedAt: string;
  period: 'daily' | 'weekly' | 'monthly';
  totals: {
    liveInputs: number;
    mediaBoxInputs: number;
    playKasterInputs: number;
    rtmpInputs: number;
    srtInputs: number;
    totalBandwidthBps: number;
    totalBytesIn: number;
    totalBytesOut: number;
    brokenOutputs: number;
    activeOutputs: number;
    channels: number;
    activeChannels?: number;
    inactiveChannels?: number;
    userAccounts?: number;
    activeUsers?: number;
    restrictedUsers?: number;
    cpuUsage?: number;
    ramUsage?: number;
  };
  longestRunningInput: null | {
    channelName?: string;
    sourceLabel?: string;
    protocol?: string;
    uptime?: number;
    bitrate?: number;
  };
  brokenOutputs: Array<{
    id?: string | null;
    channelId?: string | null;
    channelName?: string;
    type?: string;
    protocol?: string;
    name?: string;
    destination?: string;
    reason?: string;
  }>;
  groups: {
    platformsByType: Array<{ name: string; value: number }>;
    inputProtocolsByType: Array<{ name: string; value: number }>;
    outputModesByType?: Array<{ name: string; value: number }>;
    outputProtocolsByType: Array<{ name: string; value: number }>;
    inputSources: Array<{ name: string; value: number }>;
    codecs: Array<{ name: string; value: number }>;
  };
  liveStreams: Array<{
    channelName?: string;
    sourceLabel?: string;
    sourceType?: string;
    videoCodec?: string;
    audioCodec?: string;
    uptime?: number;
    bitrate?: number;
    bytesIn?: number;
    bytesOut?: number;
    outputs?: ActiveStream['outputs'];
  }>;
  history: Array<{ label: string; channels: number; outputs: number; cancellations: number }>;
}

export type FinancialReportPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly';

export interface FinancialReportsSummary {
  generatedAt: string;
  period: FinancialReportPeriod;
  range: { start: string; end: string };
  currency: string;
  totals: {
    collected: number;
    pending: number;
    failed: number;
    refunded: number;
    attempted: number;
    invoiced: number;
    receivables: number;
    paidCount: number;
    pendingCount: number;
    failedCount: number;
    refundedCount: number;
    invoiceCount: number;
    uniquePaidUsers: number;
    uniqueAttemptUsers: number;
    activeUsers: number;
    activeChannels: number;
    activeOutputs: number;
    collectionRate: number;
    failureRate: number;
    arpu: number;
    avgTransaction: number;
    revenuePerChannel: number;
    revenuePerOutput: number;
  };
  breakdowns: {
    incomeStreams: Array<{ name: string; amount: number; count: number }>;
    gateways: Array<{ name: string; amount: number; count: number }>;
    statuses: Array<{ name: string; value: number }>;
    resources: Array<{ name: string; amount: number; count: number }>;
    invoices: Array<{ name: string; value: number }>;
  };
  cancellationImpact?: AdminCancellationImpact;
  topCustomers: Array<{ userHid?: string | null; userEmail: string; amount: number; count: number }>;
  history: Array<{ label: string; collected: number; pending: number; failed: number; count: number }>;
}

export type AdminRiskLevel = 'LOW' | 'MODERATE' | 'HIGH' | 'CRITICAL';

export interface AdminRiskProfile {
  accountId: string;
  score: number;
  level: AdminRiskLevel;
  dimensions: Record<string, { score: number; max: number }>;
  factors: Array<{
    code: string;
    label: string;
    detail: string;
    points: number;
    severity: 'POSITIVE' | 'MODERATE' | 'HIGH' | 'CRITICAL' | string;
  }>;
  evidence: {
    openInvoices: number;
    overdueInvoices: number;
    outstandingByCurrency: Record<string, number>;
    failedPayments90Days: number;
    agedPendingPayments: number;
    successfulPayments90Days: number;
    totalChannels: number;
    billingSuspendedChannels: number;
  };
  recommendations: string[];
  evaluatedAt: string;
}

export interface AdminUserView {
  id: number;
  hid: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: string;
  accountStatus: 'ACTIVE' | 'DISABLED' | 'SUSPENDED' | 'CANCELLED' | 'GRACE_PERIOD' | 'PAST_DUE' | 'AWAITING_REVIEW' | 'PENDING_APPROVAL' | 'REJECTED' | 'TRIAL' | 'TRIAL_EXPIRED';
  accountStatusBeforeRestriction?: string | null;
  createdAt: string;
  billingRiskScore?: number;
  riskProfile?: AdminRiskProfile | null;
  network?: { currentConnections: number; totalDataOut: number; totalDataIn: number };
  _count?: { channels: number };
}

export interface CreateAdminUserPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  country: string;
  role: 'BROADCASTER' | 'STREAMR';
  streamName: string;
}

export interface CreateAdminUserResult {
  user: AdminUserView;
  channel: { hid: string; name: string };
}

export interface UserFilterParams { page: number; limit: number; status?: string; role?: string; search?: string }
export interface AdminTransaction {
  id: number;
  recordType?: 'PAYMENT' | 'INVOICE';
  invoiceId?: string | null;
  invoiceNumber?: string | null;
  amount: number;
  currency: string;
  status: string;
  date: string;
  gatewayTxId?: string | null;
  transactionId?: string | null;
  providerReference?: string | null;
  description: string;
  displayDescription?: string;
  incomeStream?: string;
  incomeStreamLabel?: string;
  resourceTypes?: string[];
  originalAmount?: number;
  originalCurrency?: string;
  displayCurrency?: string;
  gateway?: string;
  processed?: boolean;
  effectApplied?: boolean;
  userEmail: string;
  userId: number;
  userHid?: string | null;
  metadata?: Record<string, any>;
}
export interface AdminTransactionSummary { totalRevenue: number; pendingRevenue: number; failedRevenue: number; completedCount: number; pendingCount: number; failedCount: number; count: number }
export interface AdminIncomeStreamBreakdown { key: string; label: string; amount: number; count: number }
export interface AdminIncomeStreamOption { key: string; label: string }
export interface AdminCancellationImpact {
  currency: string;
  totals: { count: number; monthlyLostRevenue: number; channels: number; outputs: number; cpu: number; hls: number; hlsPlus: number };
  items: Array<{ id: number; userHid: string; userEmail?: string | null; channelId?: string | null; channelName?: string | null; resourceType: string; quantity: number; effectiveAt?: string | null; reason?: string | null; monthlyLostRevenue: number; currency: string; relief: { channels: number; outputs: number; cpu: number; hls: number; hlsPlus: number } }>;
}
export interface AdminTransactionMutationPayload {
  userIdentifier?: string;
  amount?: number;
  currency?: string;
  status?: string;
  description?: string;
  displayDescription?: string;
  gatewayTxId?: string;
  gateway?: 'EFT' | 'MANUAL';
  paymentType?: string;
  paymentAccount?: string;
  paymentDateTime?: string;
  incomeStream?: string;
  resourceType?: string;
  quantity?: number;
  processed?: boolean;
  effectApplied?: boolean;
  reason: string;
  note?: string;
}
export interface ManualPaymentPayload {
  amount: number;
  currency?: string;
  description: string;
  action: 'RENEW' | 'ADD_RESOURCE';
  targetPlan?: string;
  renewalTarget?: string;
  billingMode?: 'PRORATED' | 'NEW_SERVICE_DATE';
  paymentType?: string;
  paymentAccount?: string;
  paymentDateTime?: string;
  note?: string;
  reason?: string;
  items?: Array<{
    resourceType: string;
    quantity: number;
    targetChannelId?: number;
    targetTranscoderId?: number;
    customName?: string;
    tierCode?: string;
  }>;
}
export interface ManualPaymentCatalogResource {
  key: string;
  resourceType: string;
  label: string;
  description?: string;
  unitPrice: number;
  setupPrice?: number;
  requiresChannel?: boolean;
  allowQuantity?: boolean;
  singleUnit?: boolean;
  tierCode?: string;
  socialPricing?: {
    initialPrice: number;
    additionalPrice: number;
    batchSize: number;
  };
}
export interface ManualPaymentCatalog {
  currency: string;
  renewalAmount: number;
  resources: ManualPaymentCatalogResource[];
}
export interface ChannelBillingDateChange {
  channelId: string;
  previousBillingDate: string;
  billingDate: string;
  effectiveBillingDate: string;
  pendingPayment: boolean;
  prorated: boolean;
  administrativeOverride?: boolean;
  prorationDays: number;
  prorationAmountMinor: number;
  currency: string;
  invoice?: { invoiceId: string; invoiceNumber?: string; status: string } | null;
}
export interface AccountFinancialProfile {
  accountId: string;
  firstPurchaseDate: string;
  firstPurchaseOverridden: boolean;
  overrideReason?: string;
  updatedAt: string;
}
export interface AdminBillingResource {
  itemId: string;
  productCode: string;
  name: string;
  quantity: number;
  unitAmountMinor: number;
  billingBehavior: string;
  startDate: string;
  endDate?: string | null;
  status: 'ACTIVE' | 'SCHEDULED' | 'ENDED' | string;
  reason?: string;
  admin?: string;
  createdAt?: string;
  updatedAt?: string;
  provisionId?: string;
  provisionStatus?: string;
  paymentDeadline?: string;
  invoiceId?: string;
  invoiceNumber?: string;
}
export interface AdminBillingResourceCatalogProduct {
  code: string;
  name: string;
  category: string;
  description: string;
  monthlyUsd: number;
  setupUsd: number;
  quantityMode: string;
  package: boolean;
}
export interface AdminBillingResourceConfiguration {
  resources: AdminBillingResource[];
  entitlement: Record<string, any>;
  catalog: { products: AdminBillingResourceCatalogProduct[] };
}
export interface BillingDateChangeHistory {
  changeId: string;
  channelId: string;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceStatus?: string;
  previousBillingDate: string;
  billingDate: string;
  prorationAmountMinor: number;
  invoiceAmountPaidMinor: number;
  currency: string;
  status: 'PENDING_PAYMENT' | 'APPLIED' | 'CANCELLED' | 'REVERSED' | 'REVIEW_REQUIRED' | string;
  reason: string;
  reversalReason?: string;
  createdAt: string;
  updatedAt: string;
  appliedAt?: string;
  cancelledAt?: string;
  reversedAt?: string;
}
export interface BillingDateUndoResult {
  channelId: string;
  change: BillingDateChangeHistory;
  effectiveBillingDate: string;
}
export interface AuditLogEntry { id: number; action: string; details: string; createdAt: string; userId: number; user?: { email: string; role: string } }

export const getGlobalAuditLogs = async (page = 1, limit = 50, search = '') => {
  const { data } = await api.get<{ logs: AuditLogEntry[]; total: number; totalPages: number }>(`/admin/system/audit-logs?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
  return data;
};

export const getGlobalTransactions = async (
  page = 1,
  limit = 25,
  filters: { status?: string; source?: string; search?: string; currency?: string; dateRange?: string } = {}
) => {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  if (filters.status && filters.status !== 'all' && filters.status !== 'provisioning') query.set('status', filters.status);
  if (filters.source && filters.source !== 'all') query.set('source', filters.source);
  if (filters.search) query.set('search', filters.search);
  if (filters.currency) query.set('currency', filters.currency);
  if (filters.dateRange && filters.dateRange !== 'all') query.set('dateRange', filters.dateRange);
  const { data } = await api.get<{
    payments: AdminTransaction[];
    total: number;
    totalPages: number;
    summary?: AdminTransactionSummary;
    sourceBreakdown?: AdminIncomeStreamBreakdown[];
    incomeStreams?: AdminIncomeStreamOption[];
    cancellationImpact?: AdminCancellationImpact;
  }>(`/admin/financials/transactions?${query}`);
  return data;
};
export const sendInvoiceToClientAdmin = async (invoiceReference: string, reason: string) => {
  const { data } = await api.post<{
    ok: boolean;
    invoiceNumber?: string | null;
    recipient: string;
    message: string;
  }>(`/admin/financials/invoices/${encodeURIComponent(invoiceReference)}/send`, { reason });
  return data;
};
export const getFinancialReports = async (
  period: FinancialReportPeriod = 'monthly',
  currency?: string
) => {
  const query = new URLSearchParams({ period });
  if (currency) query.set('currency', currency);
  const { data } = await api.get<FinancialReportsSummary>(`/admin/financials/reports?${query}`);
  return data;
};
export const createFinancialTransaction = async (payload: AdminTransactionMutationPayload) => {
  const { data } = await api.post<AdminTransaction>('/admin/financials/transactions', payload);
  return data;
};
export const updateFinancialTransaction = async (id: number, payload: AdminTransactionMutationPayload) => {
  const { data } = await api.patch<AdminTransaction>(`/admin/financials/transactions/${id}`, payload);
  return data;
};
export const deleteFinancialTransaction = async (id: number, reason: string) => {
  const { data } = await api.delete<{ success: boolean; id: number }>(`/admin/financials/transactions/${id}`, { data: { reason } });
  return data;
};
export const getAdminReportsSummary = async () => {
  const { data } = await api.get<AdminReportsSummary>('/admin/reports/summary');
  return data;
};

export const getGoSystemStatus = async () => {
  const { data } = await api.get<GoSystemStatus>('/admin/system/status');
  return data;
};

export const getAdminUsers = async (params: UserFilterParams) => {
  const query = new URLSearchParams({ page: params.page.toString(), limit: params.limit.toString(), ...(params.status && params.status !== 'all' && { status: params.status }), ...(params.role && params.role !== 'all' && { role: params.role }), ...(params.search && { search: params.search }) });
  const { data } = await api.get<{ users: AdminUserView[]; total: number; totalPages: number }>(`/admin/users?${query}`);
  return data;
};

export const createAdminUser = async (payload: CreateAdminUserPayload) => {
  const { data } = await api.post<CreateAdminUserResult>('/admin/users', payload);
  return data;
};

export const archiveUserAdmin = async (idOrHid: AdminUserId, reason: string, confirmation = 'ARCHIVE') => {
  const { data } = await api.delete<AdminUserView>(`/admin/users/${idOrHid}`, { data: { reason, confirmation } });
  return data;
};

export const getGranularUserDetails = async (idOrHid: AdminUserId) => {
  const { data } = await api.get(`/admin/users/${idOrHid}`);
  return data;
};

export const approveUser = async (idOrHid: AdminUserId, reason?: string) => api.post(`/admin/users/${idOrHid}/approve`, { reason });
export const rejectUser = async (idOrHid: AdminUserId, reason: string) => api.post(`/admin/users/${idOrHid}/reject`, { reason });
export const updateUserAdmin = async (idOrHid: AdminUserId, updates: Partial<AdminUserView> & { reason?: string }) => api.patch(`/admin/users/${idOrHid}`, updates);
export const extendUserTrialAdmin = async (idOrHid: AdminUserId, days: number, reason: string) => {
  const { data } = await api.post<{
    user: AdminUserView & { trialExpiresAt?: string | null };
    daysAdded: number;
    previousTrialExpiresAt: string;
    trialExpiresAt: string;
  }>(`/admin/users/${idOrHid}/trial-extension`, { days, reason });
  return data;
};
export const requestUserPasswordChangeAdmin = async (idOrHid: AdminUserId, reason: string) => {
  const { data } = await api.post<{ message: string; userHid: string }>(`/admin/users/${idOrHid}/password-change`, { reason });
  return data;
};
export const disableUserAdmin = async (idOrHid: AdminUserId, reason: string) => updateUserAdmin(idOrHid, { accountStatus: 'DISABLED', reason } as any);
export const enableUserAdmin = async (idOrHid: AdminUserId, reason: string, status: 'ACTIVE' | 'TRIAL' = 'ACTIVE') => updateUserAdmin(idOrHid, { accountStatus: status, reason } as any);
export const impersonateUser = async (idOrHid: AdminUserId, reason: string) => {
  const { data } = await api.post<{ accessToken: string; refreshToken: string }>(`/admin/users/${idOrHid}/impersonate`, { reason });
  return data;
};
export const applyManualPayment = async (idOrHid: AdminUserId, payload: ManualPaymentPayload) => {
  const { data } = await api.post(`/admin/users/${idOrHid}/manual-payment`, payload);
  return data;
};
export const getManualPaymentCatalog = async (idOrHid: AdminUserId) => {
  const { data } = await api.get<ManualPaymentCatalog>(`/admin/users/${idOrHid}/manual-payment/catalog`);
  return data;
};
export const setChannelBillingDateAdmin = async (idOrHid: AdminUserId, billingDate: string, reason: string, prorate: boolean) => {
  const { data } = await api.post<ChannelBillingDateChange>(`/admin/channels/${idOrHid}/billing-date`, {
    billingDate,
    reason,
    prorate,
    administrativeOverride: !prorate,
  });
  return data;
};
export const setFirstPurchaseDateAdmin = async (idOrHid: AdminUserId, firstPurchaseDate: string, reason: string) => {
  const { data } = await api.post<AccountFinancialProfile>(`/admin/users/${idOrHid}/first-purchase-date`, { firstPurchaseDate, reason });
  return data;
};
export const clearFirstPurchaseDateAdmin = async (idOrHid: AdminUserId, reason: string) => {
  const { data } = await api.delete<AccountFinancialProfile>(`/admin/users/${idOrHid}/first-purchase-date`, { data: { reason } });
  return data;
};
export const getChannelAdminResources = async (idOrHid: AdminUserId) => {
  const { data } = await api.get<AdminBillingResourceConfiguration>(`/admin/channels/${idOrHid}/billing-resources`);
  return data;
};
export const createChannelAdminResource = async (idOrHid: AdminUserId, payload: { productCodes: string[]; reason: string }) => {
  const { data } = await api.post(`/admin/channels/${idOrHid}/billing-resources`, payload);
  return data;
};
export const updateChannelAdminResource = async (idOrHid: AdminUserId, itemId: string, payload: { productCode: string; quantity: number; startDate: string; reason: string }) => {
  const { data } = await api.patch(`/admin/channels/${idOrHid}/billing-resources/${encodeURIComponent(itemId)}`, payload);
  return data;
};
export const deleteChannelAdminResource = async (
  idOrHid: AdminUserId,
  itemId: string,
  payload: { reason: string; mode: 'SCHEDULED' | 'IMMEDIATE'; confirmation?: string },
) => {
  const { data } = await api.delete(`/admin/channels/${idOrHid}/billing-resources/${encodeURIComponent(itemId)}`, { data: payload });
  return data;
};
export const getChannelBillingDateChangesAdmin = async (idOrHid: AdminUserId) => {
  const { data } = await api.get<{ changes: BillingDateChangeHistory[] }>(`/admin/channels/${idOrHid}/billing-date-changes`);
  return Array.isArray(data?.changes) ? data.changes : [];
};
export const undoChannelBillingDateChangeAdmin = async (idOrHid: AdminUserId, changeId: string, reason: string) => {
  const { data } = await api.post<BillingDateUndoResult>(`/admin/channels/${idOrHid}/billing-date-changes/${encodeURIComponent(changeId)}/undo`, { reason });
  return data;
};
export const terminateUserAdmin = async (idOrHid: AdminUserId, payload: { mode: 'TERMINATE' | 'BAN'; reason: string }) => {
  const { data } = await api.post(`/admin/users/${idOrHid}/terminate`, payload);
  return data;
};
export const restoreUserAdmin = async (idOrHid: AdminUserId, reason: string) => {
  const { data } = await api.post(`/admin/users/${idOrHid}/restore`, { reason });
  return data;
};
export interface AdminOutputDisableResult {
  requestedCount: number;
  disabledCount: number;
  alreadyDisabledCount: number;
  outputs: Array<{ id: string; active: false }>;
}

export interface AdminChannelEventPage {
  items: Array<{ action: string; details: string; createdAt?: string | null }>;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const getAdminActiveStreams = async (): Promise<ActiveStream[]> => {
  const { data } = await api.get<ActiveStream[]>('/admin/active-streams');
  return data;
};
export const getAdminChannelInspection = async (channelId: string | number): Promise<ActiveStream> => {
  const { data } = await api.get<ActiveStream>(`/admin/channels/${channelId}/inspection`);
  return data;
};
export const getAdminChannelInspectionEvents = async (channelId: string | number, page = 1, limit = 10): Promise<AdminChannelEventPage> => {
  const { data } = await api.get<AdminChannelEventPage>(`/admin/channels/${channelId}/inspection/events`, { params: { page, limit } });
  return {
    items: Array.isArray(data?.items) ? data.items : [],
    page: Number(data?.page || page),
    limit: Number(data?.limit || limit),
    total: Number(data?.total || 0),
    totalPages: Math.max(1, Number(data?.totalPages || 1)),
  };
};
export const disableAdminChannelOutputs = async (channelId: string | number, outputIds: string[], reason: string) => {
  const { data } = await api.post<AdminOutputDisableResult>(`/admin/channels/${channelId}/outputs/disable`, { outputIds, reason });
  return data;
};
export type AdminOutputAction = 'ENABLE' | 'DISABLE' | 'RESTART' | 'DELETE';
export const manageAdminChannelOutputs = async (
  channelId: string | number,
  outputIds: string[],
  action: AdminOutputAction,
  reason: string,
  confirmation?: string,
) => {
  const { data } = await api.post(`/admin/channels/${channelId}/outputs/actions`, { outputIds, action, reason, confirmation });
  return data;
};
export const updateAdminChannelOutput = async (
  channelId: string | number,
  outputId: string,
  updates: Record<string, string | number | boolean | null>,
  reason: string,
) => {
  const { data } = await api.patch(`/admin/channels/${channelId}/outputs/${outputId}`, { updates, reason });
  return data;
};
export const interruptAdminChannelInput = async (channelId: string | number, reason: string) => {
  const { data } = await api.post(`/admin/channels/${channelId}/input/interrupt`, { reason });
  return data;
};
export const scheduleAdminChannelDeletion = async (channelId: string | number, reason: string, confirmation: string) => {
  const { data } = await api.delete(`/admin/channels/${channelId}`, { data: { reason, confirmation } });
  return data;
};
export const disableAdminUserOutputs = async (userId: string | number, outputIds: string[], reason: string) => {
  const { data } = await api.post<AdminOutputDisableResult>(`/admin/users/${userId}/outputs/disable`, { outputIds, reason });
  return data;
};
export const getNocReports = async (period: 'daily' | 'weekly' | 'monthly' = 'daily') => {
  const { data } = await api.get<NocReportsSummary>(`/admin/streams/reports?period=${encodeURIComponent(period)}`);
  return data;
};
export const killStream = async (sourceId: string, reason?: string) => api.post(`/admin/streams/${sourceId}/stop`, { reason });
export const suspendChannel = async (channelId: string | number, reason: string) => api.post(`/admin/channels/${channelId}/suspend`, { reason });
export const unsuspendChannel = async (channelId: string | number, reason: string) => api.post(`/admin/channels/${channelId}/unsuspend`, { reason });
export const getAdminSummary = async (): Promise<DashboardSummary> => {
  const { data } = await api.get('/admin/summary');
  return data;
};
